// Code injected into EPUB iframes to bootstrap the epub-iframe module.
//
// The three payload functions (syntaxCatcher, asyncLoader, errorHandler) are
// serialized with Function.prototype.toString() and executed untranspiled
// inside the iframe, so they MUST stay self-contained:
//  - no reference to anything from module scope (imports, helpers, constants):
//    all inputs come in as parameters, JSON-interpolated by serializeScript
//  - syntaxCatcher/errorHandler: keep to plain ES2015 (no async, arrow,
//    template literal, ...) — they are the safety net that reports errors
//    from browsers too old to parse asyncLoader
//  - asyncLoader: ES2017 syntax + dynamic import() (Safari 11.1 floor);
//    requires the client build target to be at least es2017 so async/await
//    isn't down-leveled into module-scope helpers (see vite.config.ts)
// These constraints are enforced by injected.build.test.ts against both the
// dev-served source and a real minified build output.

interface InjectedWindow extends Window {
  __DM_INJECT?: Promise<unknown>;
}

declare const window: InjectedWindow;

// placeholder for a dynamic import() call
// to prevent rolldown from transforming every `import(...)`
// serializeScript swaps it back for the real `import` keyword.
declare const __DM_IMPORT__: (url: string) => Promise<unknown>;

// if a syntax error occurs in the next (more modern) script block, it is
// caught here and reported to the parent window by errorHandler
export function syntaxCatcher(): void {
  window.onerror = function (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error
  ) {
    let codeline: string | null = null;
    if (source === window.location.href) {
      try {
        codeline =
          document.documentElement.outerHTML.split("\n")[(lineno || 0) - 1] ||
          null;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- an optional catch binding would be ES2019 syntax
      } catch (e) {
        // the error report is still useful without the code line
      }
    }
    window.__DM_INJECT = Promise.reject({
      message: message,
      file: source,
      lineno: lineno,
      colno: colno,
      error: error,
      codeline: codeline,
    });
    return true;
  };
}

// loads epub-iframe module(s)
// Chrome doesn't support non-async module scripts in xhtml
// see: https://issues.chromium.org/issues/40518469
// Safari doesn't support es module scripts at all in xhtml
// however, both of them allow the `import` function in a normal script
// (URLs are absolute because of the iframe's origin)
export function asyncLoader(scriptUrls: string[]): void {
  window.__DM_INJECT = (async function () {
    try {
      for (const url of scriptUrls) {
        await __DM_IMPORT__(url);
      }
      Array.prototype.forEach.call(
        document.querySelectorAll(
          'link[rel="modulepreload"][data-dm-inject="true"]'
        ),
        function (s: Element) {
          if (s.parentNode) s.parentNode.removeChild(s);
        }
      );
    } catch (e) {
      return Promise.reject(e);
    }
  })();
}

// checks that the loader did run and reports the result to the parent window,
// then cleans up every script injected (no need to keep tags around after execution)
// this part is even more conservative than syntaxCatcher: its minimal
// requirements are postMessage and Promise.reject (Safari 8)
export function errorHandler(targetOrigin: string): void {
  window.onerror = null;
  if (!window.__DM_INJECT) {
    window.__DM_INJECT = Promise.reject(
      new Error("__DM_INJECT code failed to execute")
    );
  }
  window.__DM_INJECT
    .then(
      function () {
        return { type: "dm-inject-success", _readium: true, _channel: null };
      },
      function (err: unknown) {
        return {
          type: "dm-inject-error",
          error: err,
          _readium: true,
          _channel: null,
        };
      }
    )
    .then(function (msg: { type: string; error?: unknown }) {
      function serializeError(err: Error): object {
        return {
          message: err.message,
          name: err.name,
          stack: err.stack,
          cause: err.cause && serializeError(err.cause as Error),
        };
      }
      try {
        window.parent.postMessage(msg, targetOrigin);
      } catch (e) {
        if (!msg.error) return;

        // msg is probably not serializable
        // (structured clone of an Error fails in some browsers),
        // retry with a serialized error
        console.error("Failed to postMessage to parent window", msg, e);
        if (msg.error instanceof Error) {
          msg.error = serializeError(msg.error);
        } else if ((msg.error as { error?: unknown }).error instanceof Error) {
          (msg.error as { error: unknown }).error = serializeError(
            (msg.error as { error: Error }).error
          );
        } else return;
        window.parent.postMessage(msg, targetOrigin);
      }
    });
  delete window.__DM_INJECT;
  Array.prototype.forEach.call(
    document.querySelectorAll('script[data-dm-inject="true"]'),
    function (s: Element) {
      if (s.parentNode) s.parentNode.removeChild(s);
    }
  );
}

type Json = string | number | boolean | null | Json[] | { [key: string]: Json };

// serializes a payload function into an inline script tag calling it with the
// given arguments; CDATA keeps the code valid in XML documents (XHTML)
export function serializeScript<A extends Json[]>(
  fn: (...args: A) => void,
  ...args: A
): string {
  const source = fn.toString().replace(/\b__DM_IMPORT__\b/g, "import");
  const call = `(${source})(${args
    .map((arg) => JSON.stringify(arg))
    .join(", ")});`;
  // "]]>" would close the CDATA section (XML parsing) and "</script" would
  // close the script element (HTML parsing)
  if (call.includes("]]>") || /<\/script/i.test(call)) {
    throw new Error("injected code is not XML-safe");
  }
  return `
    <script data-dm-inject="true">
      //<![CDATA[
      ${call}
      //]]>
    </script>`;
}

// escapes a URL for interpolation into a double-quoted XML attribute:
// new URL(...).href applies the exact percent-encoding the browser would use
// when fetching the href (quotes, spaces, "<", ">", ...), so the modulepreload
// fetch and the later import() stay consistent; the only XML-unsafe character
// it leaves raw is "&", which must become an entity since percent-encoding it
// would change the query string
function escape(url: URL): string {
  return url.href.replace(/&/g, "&amp;");
}

// the three script tags MUST stay separate: a syntax error in one block only
// kills that block, and the surrounding (more conservative) blocks catch and
// report it
export function render(
  scriptUrls: URL[],
  targetOrigin: string,
  fontsCssHref?: URL
): string {
  return [
    fontsCssHref
      ? `<link rel="stylesheet" href="${escape(fontsCssHref)}" data-dm-inject="true" />`
      : "",
    ...scriptUrls.map(
      (script) =>
        `<link rel="modulepreload" href="${escape(script)}" data-dm-inject="true" />`
    ),
    serializeScript(syntaxCatcher),
    serializeScript(
      asyncLoader,
      scriptUrls.map((s) => s.href)
    ),
    serializeScript(errorHandler, targetOrigin),
  ].join("\n");
}
